import { useFormikContext } from 'formik';

import { ErrorField } from '@/components/error-field';
import { KeyValuePairCard } from '@/components/key-value-pair-card';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';

import type { AgentUpdateFormValues } from '@/schemas/agent.schema';

import { EventsCard } from './events-card';

const EMPTY_PAIRS: { key: string; value: string }[] = [];

export const WebhookSettings = () => {
  const { values, getFieldProps, errors } = useFormikContext<AgentUpdateFormValues>();
  const webhookConfig = values.webhook_config;

  const headers = webhookConfig?.headers ?? EMPTY_PAIRS;
  const queryParams = webhookConfig?.query_params ?? EMPTY_PAIRS;

  return (
    <>
      {/* API Endpoint Card */}
      <Card className="bg-muted rounded-2xl gap-0 p-1 border-0 shadow-none">
        <CardHeader className="px-5 py-3">
          <CardTitle className="text-sm font-semibold">Webhook URL</CardTitle>
          <p className="text-sm text-muted-foreground">Webhook URL to receive events from LIKA.</p>
        </CardHeader>
        <CardContent className="bg-card rounded-xl shadow-sm p-5">
          <Field aria-invalid={!!errors['webhook_config']?.['url']}>
            <div className="flex gap-3">
              <Input
                placeholder="Enter the webhook URL"
                className="flex-1"
                aria-invalid={!!errors['webhook_config']?.['url']}
                {...getFieldProps('webhook_config.url')}
              />
            </div>
            <ErrorField name="webhook_config.url" />
          </Field>
        </CardContent>
      </Card>

      {/* Timeout Card */}
      <Card className="bg-muted rounded-2xl gap-0 p-1 border-0 shadow-none">
        <CardHeader className="px-5 py-3">
          <CardTitle className="text-sm font-semibold">Timeout (ms)</CardTitle>
        </CardHeader>
        <CardContent className="bg-card rounded-xl shadow-sm p-5">
          <div className="relative">
            <Input
              type="number"
              placeholder="Input Value"
              {...getFieldProps('webhook_config.timeout')}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
              milliseconds
            </span>
          </div>
          <ErrorField name="webhook_config.timeout" />
        </CardContent>
      </Card>

      {/* Headers Card */}
      <KeyValuePairCard
        name="webhook_config.headers"
        title="Headers"
        description="Specify the HTTP headers required for your API request"
        items={headers}
      />

      {/* Query Parameters Card */}
      <KeyValuePairCard
        name="webhook_config.query_params"
        title="Query Parameters"
        description="Query string parameters to append to the URL."
        items={queryParams}
      />

      {/* Events Card */}
      <EventsCard />
    </>
  );
};
